// Unified task ordering used across queue modules (Manager Approval, Developer Queue,
// UI/UX Queue) and any other tabbed task view. Centralising the logic here guarantees
// identical behaviour across pagination, filters, search results, grouped views and
// keeps the order deterministic after status changes / approvals / edits / refreshes.
//
// Rules (driven by the workflow-priority spec):
//   • Yet to Start     → oldest pending first (asc by start_date, fallback created_at)
//   • In Progress      → most recently updated first (desc by updated_at)
//   • Pending Approval → most recently updated first (desc by updated_at)
//   • Completed        → most recently completed first (desc by completion date)
//
// All sorts apply a stable secondary tie-break (task id asc) to prevent random
// re-shuffling when two tasks share the same timestamp.

const toMs = (value) => {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const stableTieBreak = (a, b) => {
  const aId = String(a?.id ?? '');
  const bId = String(b?.id ?? '');
  if (aId < bId) return -1;
  if (aId > bId) return 1;
  return 0;
};

const DEFAULT_COMPLETED_FIELDS = [
  'developer_end_date',
  'design_end_date',
  'your_end_date',
  'updated_at',
];

const firstCompletionTs = (task, fields) => {
  for (const f of fields) {
    const ms = toMs(task?.[f]);
    if (ms !== null) return ms;
  }
  return null;
};

/**
 * Sort a list of tasks according to the workflow-priority rule for the given tab.
 *
 * @param {Array<Object>} tasks
 * @param {'Yet to Start'|'In Progress'|'Pending Approval'|'Completed'} tabStatus
 * @param {{ completedDateFields?: string[] }} [options]
 *   completedDateFields — ordered list of fields consulted for "completion" (first
 *   non-null wins). Defaults to ['developer_end_date','design_end_date',
 *   'your_end_date','updated_at'] so each queue can customise (e.g. dev queue
 *   prioritises developer_end_date).
 * @returns {Array<Object>} a new sorted array (input is not mutated).
 */
export function sortTasksByTabStatus(tasks, tabStatus, options = {}) {
  if (!Array.isArray(tasks) || tasks.length === 0) return [];
  const list = tasks.slice();
  const completedFields = options.completedDateFields || DEFAULT_COMPLETED_FIELDS;

  if (tabStatus === 'Yet to Start') {
    list.sort((a, b) => {
      const aMs = toMs(a.start_date) ?? toMs(a.created_at);
      const bMs = toMs(b.start_date) ?? toMs(b.created_at);
      // Tasks with no date sink to the bottom (treated as +Infinity for asc).
      const aKey = aMs ?? Number.POSITIVE_INFINITY;
      const bKey = bMs ?? Number.POSITIVE_INFINITY;
      if (aKey !== bKey) return aKey - bKey;
      return stableTieBreak(a, b);
    });
    return list;
  }

  if (tabStatus === 'In Progress' || tabStatus === 'Pending Approval') {
    list.sort((a, b) => {
      const aMs = toMs(a.updated_at) ?? toMs(a.created_at);
      const bMs = toMs(b.updated_at) ?? toMs(b.created_at);
      // Tasks with no date sink to the bottom (treated as -Infinity for desc).
      const aKey = aMs ?? Number.NEGATIVE_INFINITY;
      const bKey = bMs ?? Number.NEGATIVE_INFINITY;
      if (aKey !== bKey) return bKey - aKey;
      return stableTieBreak(a, b);
    });
    return list;
  }

  if (tabStatus === 'Completed') {
    list.sort((a, b) => {
      const aMs = firstCompletionTs(a, completedFields);
      const bMs = firstCompletionTs(b, completedFields);
      const aKey = aMs ?? Number.NEGATIVE_INFINITY;
      const bKey = bMs ?? Number.NEGATIVE_INFINITY;
      if (aKey !== bKey) return bKey - aKey;
      return stableTieBreak(a, b);
    });
    return list;
  }

  // Unknown tab — return list unchanged (still stable via natural order).
  return list;
}

export const ORDERING_LABELS = {
  'Yet to Start': 'Oldest pending first',
  'In Progress': 'Recently updated first',
  'Pending Approval': 'Recently updated first',
  'Completed': 'Recently completed first',
};

export function getOrderingLabel(tabStatus) {
  return ORDERING_LABELS[tabStatus] || '';
}
